import { useEffect, useRef, useState } from "react";
import { AlertCircle, Calendar, Loader2 } from "lucide-react";

const CALENDLY_URL = (process.env.REACT_APP_CALENDLY_URL || "").trim();
const LIVE = CALENDLY_URL.length > 0;

function ensureCss() {
  if (document.querySelector("link[data-calendly-css]")) return;
  const link = document.createElement("link");
  link.href = "https://assets.calendly.com/assets/external/widget.css";
  link.rel = "stylesheet";
  link.dataset.calendlyCss = "true";
  document.head.appendChild(link);
}

function loadCalendly() {
  ensureCss();
  if (window.Calendly) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector("script[data-calendly]");
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("Calendly failed to load"))
      );
      return;
    }
    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    script.dataset.calendly = "true";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Calendly failed to load"));
    document.body.appendChild(script);
  });
}

function scrollToEmbed() {
  const el = document.getElementById("booking-embed");
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
  else el.scrollIntoView({ behavior: "smooth" });
}

export default function CalendlyBooking() {
  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(false);
  const inlineRef = useRef(null);

  useEffect(() => {
    if (!LIVE) return undefined;
    let cancelled = false;
    let poll = null;
    loadCalendly()
      .then(() => {
        if (cancelled || !inlineRef.current || !window.Calendly) return;
        inlineRef.current.innerHTML = "";
        window.Calendly.initInlineWidget({
          url: CALENDLY_URL,
          parentElement: inlineRef.current,
        });
        let tries = 0;
        poll = setInterval(() => {
          tries += 1;
          if (inlineRef.current && inlineRef.current.querySelector("iframe")) {
            setReady(true);
            clearInterval(poll);
          } else if (tries > 40) {
            clearInterval(poll);
            setFailed(true);
          }
        }, 300);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (poll) clearInterval(poll);
      if (inlineRef.current) inlineRef.current.innerHTML = "";
    };
  }, []);

  const onButtonClick = () => {
    if (!LIVE) {
      scrollToEmbed();
      return;
    }
    loadCalendly()
      .then(() => {
        if (window.Calendly) {
          window.Calendly.initPopupWidget({ url: CALENDLY_URL });
        }
      })
      .catch(() => setFailed(true));
  };

  return (
    <>
      <button
        type="button"
        data-testid="book-call-button"
        onClick={onButtonClick}
        className="mt-5 inline-flex min-h-12 items-center gap-2 rounded-lg bg-navy px-6 font-display text-base font-semibold text-white transition-colors hover:bg-navy/90"
      >
        <Calendar className="h-4 w-4" aria-hidden="true" />
        Book a 15-minute call
      </button>

      {!LIVE && (
        <div
          id="booking-embed"
          data-testid="booking-embed-slot"
          className="mt-5 rounded-lg border-2 border-dashed border-slate-300 bg-offwhite p-6 text-center"
        >
          <Calendar className="mx-auto h-6 w-6 text-slate-400" aria-hidden="true" />
          <p className="mt-2 text-sm font-semibold text-slate-500">
            Booking calendar embed slot
          </p>
          <p className="mt-1 text-xs text-slate-400">
            A scheduling calendar will be connected here.
          </p>
        </div>
      )}

      {LIVE && failed && (
        <div
          id="booking-embed"
          data-testid="booking-error-fallback"
          className="mt-5 rounded-lg border border-slate-200 bg-offwhite p-6 text-center"
        >
          <AlertCircle
            className="mx-auto h-6 w-6 text-slate-400"
            aria-hidden="true"
          />
          <p className="mt-2 text-sm text-slate-600">
            The booking calendar couldn't load right now.
          </p>
          <p className="mt-1 text-sm text-slate-600">
            Email{" "}
            <a
              href="mailto:zach@webpilot.agency"
              className="font-semibold text-skyblue underline"
            >
              zach@webpilot.agency
            </a>{" "}
            and I'll set up a time directly.
          </p>
        </div>
      )}

      {LIVE && !failed && (
        <div
          id="booking-embed"
          data-testid="calendly-inline-container"
          className="relative mt-5 w-full min-w-0 overflow-hidden rounded-lg border border-slate-200 bg-offwhite"
        >
          {!ready && (
            <div
              data-testid="booking-loading-skeleton"
              className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white"
            >
              <Loader2
                className="h-6 w-6 animate-spin text-skyblue"
                aria-hidden="true"
              />
              <p className="text-sm text-slate-500">Loading the calendar…</p>
            </div>
          )}
          <div
            ref={inlineRef}
            data-testid="calendly-inline-widget"
            className="w-full min-w-0"
            style={{ minWidth: 0, height: 620 }}
          />
        </div>
      )}
    </>
  );
}
